import { BACKEND_URL } from '../../config/backendUrl';
/* PlanPanel — Master-Admin-only subscription manager for one tenant.
 *
 * Embedded in the org-detail expand block on `MasterAdminPage.js`.
 * Reads from `GET /api/admin/tenants/{id}/plan`, writes to
 * `POST /api/admin/tenants/{id}/plan`. Plan IDs (`forms` / `documents`
 * / `complete` / `enterprise`) match the public pricing-page catalog
 * but display labels mirror the outcome-led tier names (Starter / Pro /
 * Compliance) used throughout the rest of the admin surface.
 *
 * Form pattern: catalog default fills in when the plan changes, but
 * each limit (seats / send_limit / template_limit) is editable so a
 * Master Admin can negotiate custom shapes (e.g. "Pro tier but 15
 * recruiters") without falling back to Enterprise.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { CalendarClock, Crown, Save, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

const API_URL = `${BACKEND_URL}/api`;

// Display metadata + catalog defaults. Kept here intentionally so this
// panel is self-contained — the backend `/admin/plans/catalog` endpoint
// is the canonical source if these drift.
const PLAN_OPTIONS = [
  { id: 'forms',      label: 'Starter',    sub: '£149/mo · forms only',          seats: 3,  send_limit: 250,  template_limit: 10 },
  { id: 'documents',  label: 'Pro',        sub: '£299/mo · forms + PDFs',        seats: 10, send_limit: 1000, template_limit: 25 },
  { id: 'complete',   label: 'Compliance', sub: '£599/mo · adds credentials',    seats: -1, send_limit: -1,   template_limit: -1 },
  { id: 'enterprise', label: 'Enterprise', sub: 'custom · SSO, white-label, SLA', seats: -1, send_limit: -1,   template_limit: -1 },
];

const limitDisplay = (v) => (v === -1 || v === null || v === undefined ? '∞' : v);

export default function PlanPanel({ tenantId, tenantName }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [planId, setPlanId] = useState('documents');
  // Form fields are kept as strings so empty inputs don't coerce to 0.
  const [seats, setSeats] = useState('');
  const [sendLimit, setSendLimit] = useState('');
  const [templateLimit, setTemplateLimit] = useState('');
  const [billingCycle, setBillingCycle] = useState('annual');
  const [trialEndsAt, setTrialEndsAt] = useState('');
  const [renewsAt, setRenewsAt] = useState('');
  const [notes, setNotes] = useState('');
  const [currentSub, setCurrentSub] = useState(null);

  useEffect(() => {
    if (!tenantId) return;
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API_URL}/admin/tenants/${tenantId}/plan`);
        if (cancelled) return;
        const sub = res.data?.subscription || {};
        setCurrentSub(sub);
        // Pre-fill the form with the existing subscription or fall back
        // to the Pro tier defaults so first-time edits don't show 0s.
        const pid = sub.plan_id || 'documents';
        setPlanId(pid);
        const fallback = PLAN_OPTIONS.find(p => p.id === pid) || PLAN_OPTIONS[1];
        setSeats(String(sub.seats ?? fallback.seats));
        setSendLimit(String(sub.send_limit ?? fallback.send_limit));
        setTemplateLimit(String(sub.template_limit ?? fallback.template_limit));
        setBillingCycle(sub.billing_cycle || 'annual');
        // Date inputs need YYYY-MM-DD — strip the time portion.
        setTrialEndsAt(sub.trial_ends_at ? sub.trial_ends_at.slice(0, 10) : '');
        setRenewsAt(sub.renews_at ? sub.renews_at.slice(0, 10) : '');
        setNotes(sub.notes || '');
      } catch (err) {
        console.error('Failed to load plan:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, [tenantId]);

  // When the plan ID changes, refresh limit fields to the new catalog
  // defaults — Master Admin can still override after.
  const onPlanChange = (newPlanId) => {
    setPlanId(newPlanId);
    const opt = PLAN_OPTIONS.find(p => p.id === newPlanId);
    if (opt) {
      setSeats(String(opt.seats));
      setSendLimit(String(opt.send_limit));
      setTemplateLimit(String(opt.template_limit));
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      // Inputs come in as strings; coerce to int. Empty string → null
      // tells the backend to use catalog default.
      const intOrNull = (v) => (v === '' || v === null ? null : parseInt(v, 10));
      const payload = {
        plan_id: planId,
        seats: intOrNull(seats),
        send_limit: intOrNull(sendLimit),
        template_limit: intOrNull(templateLimit),
        billing_cycle: billingCycle,
        // Date inputs are YYYY-MM-DD; backend expects ISO. Append midnight UTC.
        trial_ends_at: trialEndsAt ? `${trialEndsAt}T00:00:00+00:00` : null,
        renews_at: renewsAt ? `${renewsAt}T00:00:00+00:00` : null,
        notes: notes || null,
      };
      const res = await axios.post(`${API_URL}/admin/tenants/${tenantId}/plan`, payload);
      setCurrentSub(res.data?.subscription || null);
      toast.success('Plan updated');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to update plan');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
        <Loader2 className="w-4 h-4 animate-spin" /> Loading plan…
      </div>
    );
  }

  // Compute trial-days-left inline. The panel only mounts when an
  // org row is expanded so recomputing per render is fine.
  let trialDaysLeft = null;
  if (currentSub?.trial_ends_at) {
    const ms = new Date(currentSub.trial_ends_at).getTime() - Date.now();
    trialDaysLeft = ms > 0 ? Math.ceil(ms / (1000 * 60 * 60 * 24)) : 0;
  }

  return (
    <div className="space-y-4" data-testid="plan-panel">
      <div className="flex items-center gap-2">
        <Crown className="w-4 h-4 text-emerald-600" />
        <h4 className="font-semibold text-sm">Subscription</h4>
      </div>

      {/* Current state snapshot */}
      {currentSub?.plan_id ? (
        <div className="rounded-lg border border-slate-200 bg-white p-3 text-xs space-y-1.5">
          <div className="flex justify-between">
            <span className="text-slate-500">Plan</span>
            <span className="font-semibold text-slate-900">
              {PLAN_OPTIONS.find(o => o.id === currentSub.plan_id)?.label || currentSub.plan_id}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-500">Seats / Sends / Templates</span>
            <span className="font-mono text-slate-700">
              {limitDisplay(currentSub.seats)} · {limitDisplay(currentSub.send_limit)} · {limitDisplay(currentSub.template_limit)}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-slate-500">Billing</span>
            <span className="text-slate-700 capitalize">{currentSub.billing_cycle || '—'}</span>
          </div>
          {trialDaysLeft !== null && (
            <div className="flex justify-between items-center pt-1 mt-1 border-t border-slate-100">
              <span className="text-slate-500 flex items-center gap-1"><CalendarClock className="w-3 h-3" /> Trial</span>
              <span className={`font-semibold ${trialDaysLeft > 3 ? 'text-emerald-700' : trialDaysLeft > 0 ? 'text-amber-700' : 'text-red-700'}`}>
                {trialDaysLeft > 0 ? `${trialDaysLeft} days left` : 'expired'}
              </span>
            </div>
          )}
        </div>
      ) : (
        <p className="text-xs text-slate-500 italic">No subscription set yet — pick a plan below.</p>
      )}

      {/* Edit form */}
      <div className="space-y-3">
        <div className="space-y-1.5">
          <Label className="text-xs">Plan</Label>
          <select
            value={planId}
            onChange={(e) => onPlanChange(e.target.value)}
            className="w-full h-9 px-2.5 rounded-md border border-input bg-background text-sm"
            data-testid="plan-panel-select"
          >
            {PLAN_OPTIONS.map(o => (
              <option key={o.id} value={o.id}>{o.label} — {o.sub}</option>
            ))}
          </select>
        </div>

        <div className="grid grid-cols-3 gap-2">
          <div className="space-y-1.5">
            <Label className="text-xs">Seats</Label>
            <Input
              type="number"
              value={seats}
              onChange={(e) => setSeats(e.target.value)}
              className="h-9 text-sm"
              placeholder="-1 = ∞"
              data-testid="plan-panel-seats"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Sends / mo</Label>
            <Input
              type="number"
              value={sendLimit}
              onChange={(e) => setSendLimit(e.target.value)}
              className="h-9 text-sm"
              placeholder="-1 = ∞"
              data-testid="plan-panel-sends"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Templates</Label>
            <Input
              type="number"
              value={templateLimit}
              onChange={(e) => setTemplateLimit(e.target.value)}
              className="h-9 text-sm"
              placeholder="-1 = ∞"
              data-testid="plan-panel-templates"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2">
          <div className="space-y-1.5">
            <Label className="text-xs">Trial ends</Label>
            <Input
              type="date"
              value={trialEndsAt}
              onChange={(e) => setTrialEndsAt(e.target.value)}
              className="h-9 text-sm"
              data-testid="plan-panel-trial-ends"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Renews on</Label>
            <Input
              type="date"
              value={renewsAt}
              onChange={(e) => setRenewsAt(e.target.value)}
              className="h-9 text-sm"
              data-testid="plan-panel-renews"
            />
          </div>
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">Billing cycle</Label>
          <select
            value={billingCycle}
            onChange={(e) => setBillingCycle(e.target.value)}
            className="w-full h-9 px-2.5 rounded-md border border-input bg-background text-sm"
            data-testid="plan-panel-billing"
          >
            <option value="annual">Annual</option>
            <option value="monthly">Monthly</option>
            <option value="trial">Trial (no billing)</option>
          </select>
        </div>

        <div className="space-y-1.5">
          <Label className="text-xs">Notes (internal)</Label>
          <Input
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            className="h-9 text-sm"
            placeholder="e.g. negotiated 15 seats, custom MSA"
            data-testid="plan-panel-notes"
          />
        </div>

        <Button
          onClick={save}
          disabled={saving}
          className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
          data-testid="plan-panel-save"
        >
          {saving ? (
            <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Saving…</>
          ) : (
            <><Save className="w-4 h-4 mr-1.5" /> Save subscription</>
          )}
        </Button>
        <p className="text-[10px] text-slate-400 leading-snug">
          Tip: leave a limit blank or set <code>-1</code> for unlimited. Tenant&apos;s top-level <code>plan</code> field is also updated to keep the badge in sync.
        </p>
      </div>
    </div>
  );
}
